import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Box,
  Table,
  TableSortLabel,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TablePagination,
  Paper,
  Stack,
  Chip,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useMediaQuery,
  Typography,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  Divider,
  Container,
} from "@mui/material";

import {

  PersonOutline as PersonIcon,

} from "@mui/icons-material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  BackupTable as ExportIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import { debounce } from "@mui/material/utils";
import { toast } from "react-toastify";
import TopNav from "../components/topnav";
import { ADMIN, MEDICAL_ASSISTANT, secureApi } from "../config";
import { Context } from "../context";
import { useNavigate } from "react-router-dom";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";

// Styled components for better UI
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transition: "background-color 0.2s ease",
  },
  cursor: "pointer",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  fontSize: 14,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    boxShadow: theme.shadows[2],
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 20,
    transition: theme.transitions.create(["border-color"]),
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const CustomCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  padding: theme.spacing(0.5, 0),
  ...(status === "active" && {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.success.dark,
  }),
  ...(status === "inactive" && {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark,
  }),
}));

const SignedChip = styled(Chip)(({ theme, signed }) => ({
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  padding: theme.spacing(0.5, 0),
  ...(signed && {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.success.dark,
  }),
  ...(!signed && {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark,
  }),
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    borderRadius: 12,
  },
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

function createData(
  id,
  chart,
  patient_info,
  contact_info,
  insurance,
  employer,
  date_of_service,
  note_status,
  form_status,
  status,
  patient_notes
) {
  return {
    id,
    chart,
    patient_info,
    contact_info,
    insurance,
    employer,
    date_of_service,
    note_status,
    form_status,
    status,
    patient_notes,
  };
}

const headCells = [
  {
    id: "chart",
    label: "Chart #",
  },
  {
    id: "patientName",
    label: "Patient Name",
  },
  {
    id: "dateOfBirth",
    label: "D.O.B",
  },
  {
    id: "dateOfService",
    label: "D.O.S",
  },
  {
    id: "noteStatus",
    label: "Note Status",
  },
  {
    id: "formStatus",
    label: "Form Status",
  },
  {
    id: "status",
    label: "Status",
  },
];

const tableHeads = [
  { headerName: "First Name", field: "first_name" },
  { headerName: "Last Name", field: "last_name" },
  { headerName: "Gender", field: "gender" },
  { headerName: "Email Address", field: "email" },
  { headerName: "Mobile Phone Number", field: "contact_number" },
  { headerName: "Insurance", field: "insurance" },
  { headerName: "Prefered Contact", field: "prefered_contact" },
  { headerName: "Patient EMR ID", field: "chart" },
  { headerName: "DOB", field: "date_of_birth" },
  { headerName: "Date of Service", field: "date_of_service" },
  { headerName: "Location/Office", field: "clinic" },
  { headerName: "Referred By", field: "referredBy" },
  { headerName: "Chief Complaint", field: "pains" },
  { headerName: "Provider", field: "provider" },
  { headerName: "Employer", field: "employer" },
  { headerName: "Procedures Done with Dr Alex", field: "procedures" },
  { headerName: "Past Medical History", field: "past_medical_history" },
  { headerName: "Past Surgical History", field: "past_surgical_history" },
];

export default function Patients() {
  const { setLoggedIn, user } = useContext(Context);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [patients, setPatients] = useState([]);
  const [totalPatients, setTotalPatients] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [patientID, setPatientID] = useState();
  const [searchStr, setSearchStr] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [locations, setLocations] = useState([]);
  const [refers, setRefers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteButtonClick = (event, patient_id) => {
    event.stopPropagation();
    if (user && user.role === ADMIN) {
      setPatientID(patient_id);
      setDeleteModalOpen(true);
    } else {
      toast.error("You don't have permission to delete patients");
    }
  };

  const deletePatient = () => {
    if (user && user.role === ADMIN) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .delete(`/patient/${patientID}`)
        .then(() => {
          toast.success("Patient has been deleted successfully!");
          window.location.href = window.location.href;
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else {
            toast.error(e.response.data.message);
          }
        });
    } else {
      toast.error("You don't have permission to delete patients!");
    }

    setDeleteModalOpen(false);
  };

  const handleCancel = () => {
    setDeleteModalOpen(false);
  };

  const exportToExcel = () => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    // Convert data with headers to sheet
    const dataWithHeaders = patients.map((patient) => {
      patient["first_name"] = patient["patient_info"]["first_name"];
      patient["last_name"] = patient["patient_info"]["last_name"];
      patient["gender"] = patient["patient_info"]["gender"];
      patient["date_of_birth"] = patient["patient_info"]["date_of_birth"];
      const office_location_id = patient["patient_info"]["clinic"];
      if (office_location_id) {
        const officeLocations = locations.filter(
          (loc) => loc._id === office_location_id
        );
        if (officeLocations.length > 0) {
          patient["clinic"] = officeLocations[0].location;
        }
      }
      const referredBy = patient["patient_info"]["referredBy"];
      if (referredBy) {
        const filteredRefers = refers.filter((ref) => ref._id === referredBy);
        if (filteredRefers.length > 0) {
          patient["referredBy"] = filteredRefers[0].refer;
        }
      }
      patient["provider"] = "Dr Alex";
      patient["email"] = patient["contact_info"]["email_address"];
      patient["contact_number"] = patient["contact_info"]["contact_number"];
      patient["prefered_contact"] =
        patient["contact_info"]["preferred_contact"];
      patient["pains"] = [];
      patient["procedures"] = [];
      patient["past_medical_history"] = [];
      patient["past_surgical_history"] = [];

      patient["patient_notes"].forEach((note) => {
        if (note.pains && Array.isArray(note.pains)) {
          const painNames = [...new Set(note.pains.map((pain) => pain.name))];
          patient["pains"] = [...new Set([...patient["pains"], ...painNames])];
        }

        if (note.pastMedicalHistory && Array.isArray(note.pastMedicalHistory)) {
          patient["past_medical_history"] = [
            ...new Set([
              ...patient["past_medical_history"],
              ...note.pastMedicalHistory,
            ]),
          ];
        }

        if (
          note.pastSurgicalHistory &&
          Array.isArray(note.pastSurgicalHistory)
        ) {
          patient["past_surgical_history"] = [
            ...new Set([
              ...patient["past_surgical_history"],
              ...note.pastSurgicalHistory,
            ]),
          ];
        }

        if (
          note.performedProcedure &&
          note.performedProcedure.procedure &&
          Array.isArray(note.performedProcedure.procedure)
        ) {
          patient["procedures"] = [
            ...new Set([
              ...patient["procedures"],
              ...note.performedProcedure.procedure,
            ]),
          ];
        }
      });
      patient["pains"] = patient["pains"].join("; ");
      patient["procedures"] = patient["procedures"].join("; ");
      patient["past_medical_history"] =
        patient["past_medical_history"].join("; ");
      patient["past_surgical_history"] =
        patient["past_surgical_history"].join("; ");

      const obj = {};
      tableHeads
        .filter((column) => column.headerName)
        .forEach((column) => {
          if (column.headerName) obj[column.headerName] = patient[column.field];
        });
      return obj;
    });
    // Convert data with headers to sheet
    const ws = XLSX.utils.json_to_sheet(dataWithHeaders);

    // Set background color for the first 2 column headers
    const firstTwoHeaders = tableHeads
      .filter((column) => column.headerName)
      .slice(0, 2)
      .map((column) => column.headerName);

    firstTwoHeaders.forEach((header, index) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
      if (!ws[cellAddress]) ws[cellAddress] = { t: "s", v: header };
      ws[cellAddress].s = {
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { rgb: "FFFF00" }, // Yellow background color
        },
        font: {
          bold: true,
        },
      };
    });

    // Create workbook and add sheet with data
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "data");

    // Write workbook to buffer in xlsx format
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Convert buffer to Blob with specified MIME type
    const data = new Blob([excelBuffer], { type: fileType });

    // Save data as a file with specified filename
    FileSaver.saveAs(data, `patients` + fileExtension);
    
    // Success notification
    toast.success("Patient data exported successfully");
  };

  useEffect(() => {
    setIsLoading(true);
    secureApi(window.localStorage.getItem("alex-med-token"))
      .post(`/patients`, {
        page,
        rowsPerPage,
        searchStr,
        order,
        orderBy,
      })
      .then((response) => {
        let { patients, total_patients } = response.data;
        patients = patients.map((patient) => {
          patient["dateOfService"] = patient["dateOfService"].map(
            (dos) =>
              dos.split("/")[1] +
              "/" +
              dos.split("/")[2] +
              "/" +
              dos.split("/")[0]
          );
          if (orderBy === "dateOfService") {
            if (order === "desc") patient["dateOfService"].sort().reverse();
            else patient["dateOfService"].sort();
          }

          return createData(
            patient["_id"],
            patient["chart"],
            patient.patientInfo,
            patient.contactInfo,
            patient["insuranceInformation"]["insurance_provider"],
            patient["employment"]["employer"],
            patient["dateOfService"].join(", "),
            patient["noteStatus"],
            patient["formStatus"],
            patient["status"],
            patient["patient_notes"]
          );
        });
        setPatients(patients);
        setTotalPatients(total_patients);
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else {
          toast.error(e.response.data.message);
        }
        setIsLoading(false);
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    secureApi(window.localStorage.getItem("alex-med-token"))
      .get("/office-locations")
      .then((response) => {
        if (response.data["office_locations"]) {
          setLocations(response.data["office_locations"]);
        }
      });

    secureApi(window.localStorage.getItem("alex-med-token"))
      .get("/refers")
      .then((response) => {
        if (response.data["refers"]) {
          setRefers(response.data["refers"]);
        }
      });
  }, []);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalPatients) : 0;

  const viewPatientData = (e, patient_id) => {
    navigate(`/patient/${patient_id}`);
  };

  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        setIsLoading(true);
        secureApi(window.localStorage.getItem("alex-med-token"))
          .post(`/patients`, {
            page: 0,
            rowsPerPage,
            searchStr: request.searchStr,
            order: request.order,
            orderBy: request.orderBy,
          })
          .then((response) => {
            callback(response.data);
            setIsLoading(false);
          })
          .catch((e) => {
            if (e.response && e.response.status === 401) {
              toast.warn("Session has been expired. You need to login again!");
              setLoggedIn(false);
              navigate("/login");
            } else {
              toast.error(e.response.data.message);
            }
            setIsLoading(false);
          });
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    fetch({ searchStr: searchStr, order, orderBy }, (results) => {
      if (active) {
        let { patients, total_patients } = results;
        patients = patients.map((patient) => {
          patient["dateOfService"] = patient["dateOfService"].map(
            (dos) =>
              `${dos.split("/")[1]}/${dos.split("/")[2]}/${dos.split("/")[0]}`
          );
          if (orderBy === "dateOfService") {
            if (order === "desc") patient["dateOfService"].sort().reverse();
            else patient["dateOfService"].sort();
          }
          return createData(
            patient["_id"],
            patient["chart"],
            patient["patientInfo"],
            patient["contactInfo"],
            patient["insuranceInformation"]["insurance_provider"],
            patient["employment"]["employer"],
            patient["dateOfService"].join(", "),
            patient["noteStatus"],
            patient["formStatus"],
            patient["status"],
            patient["patient_notes"]
          );
        });
        setPatients(patients);
        setTotalPatients(total_patients);
        setPage(0);
      }
    });

    return () => {
      active = false;
    };
  }, [searchStr, fetch, order, orderBy]);

  const createSortHandler = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <>
      <TopNav item="patients" />
      <Container maxWidth="xl">
        <Box marginY={4}>
          <CustomCard>
            <CardContent>
              <PageHeader>
                <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Patient Management
                </Typography>
                {user && (user.role === ADMIN || user.role === MEDICAL_ASSISTANT) && (
                  <ActionButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => window.open("/patient", "_blank")}
                  >
                    Add New Patient
                  </ActionButton>
                )}
              </PageHeader>
              
              <Grid container spacing={2} alignItems="center" marginBottom={2}>
                <Grid item xs={12} md={8}>
                  <SearchField
                    fullWidth
                    size="small"
                    placeholder="Search by name..."
                    variant="outlined"
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" justifyContent="flex-end">
                    <Tooltip title="Export patient data to Excel">
                      <ActionButton
                        variant="outlined"
                        color="primary"
                        startIcon={<ExportIcon />}
                        onClick={exportToExcel}
                      >
                        Export to Excel
                      </ActionButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ mb: 2 }} />
              
              <Paper elevation={0} sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer sx={{ maxHeight: 650 }}>
                  <Table stickyHeader aria-label="patient table">
                    <TableHead>
                      <TableRow>
                        {headCells.map((headCell) => (
                          <HeaderTableCell
                            key={headCell.id}
                            padding={"normal"}
                            sortDirection={orderBy === headCell.id ? order : false}
                          >
                            {headCell.id === "patientName" ||
                            headCell.id === "dateOfBirth" ||
                            headCell.id === "dateOfService" ? (
                              <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : "asc"}
                                onClick={(e) => createSortHandler(e, headCell.id)}
                                sx={{ color: "white !important" }}
                              >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                  <Box component="span" sx={visuallyHidden}>
                                    {order === "desc"
                                      ? "sorted descending"
                                      : "sorted ascending"}
                                  </Box>
                                ) : null}
                              </TableSortLabel>
                            ) : (
                              headCell.label
                            )}
                          </HeaderTableCell>
                        ))}
                        {user.role === ADMIN && (
                          <HeaderTableCell
                            key="operation"
                            padding={"normal"}
                          >
                            Actions
                          </HeaderTableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                            <Typography>Loading patient data...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : patients.length > 0 ? (
                        patients.map((row) => (
                          <StyledTableRow
                            onClick={(event) => viewPatientData(event, row["id"])}
                            key={row.id}
                          >
                            <StyledTableCell>{row.chart}</StyledTableCell>
                            <StyledTableCell sx={{ fontWeight: 500 }}>
                              {`${row.patient_info.first_name} ${row.patient_info.last_name}`}
                            </StyledTableCell>
                            <StyledTableCell>{row.patient_info.date_of_birth}</StyledTableCell>
                            <StyledTableCell>{row.date_of_service}</StyledTableCell>
                            <StyledTableCell>
                              <SignedChip 
                                label={row.note_status ? "Signed" : "Unsigned"} 
                                signed={row.note_status} 
                                size="small"
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <SignedChip 
                                label={row.form_status ? "Signed" : "Unsigned"} 
                                signed={row.form_status} 
                                size="small"
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <StatusChip 
                                label={row.status} 
                                status={row.status} 
                                size="small"
                              />
                            </StyledTableCell>
                            {user.role === ADMIN && (
                              <StyledTableCell>
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title="View patient details">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        viewPatientData(e, row["id"]);
                                      }}
                                    >
                                      <ViewIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete patient">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => handleDeleteButtonClick(e, row["id"])}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </StyledTableCell>
                            )}
                          </StyledTableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell 
                            colSpan={8} 
                            align="center" 
                            sx={{ py: 5 }}
                          >
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                              <WarningIcon color="action" sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                              <Typography variant="h6" gutterBottom color="textSecondary">
                                No patients found
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                {searchStr ? 
                                  "No patients match your search criteria." : 
                                  "There are no patients in the system yet."}
                              </Typography>
                              {user && (user.role === ADMIN || user.role === MEDICAL_ASSISTANT) && (
                                <Button 
                                  variant="outlined" 
                                  startIcon={<AddIcon />}
                                  onClick={() => window.open("/patient", "_blank")}
                                  sx={{ mt: 1 }}
                                >
                                  Add Patient
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100, { label: "All", value: -1 }]}
                  component="div"
                  count={totalPatients}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ borderTop: 1, borderColor: 'divider' }}
                />
              </Paper>
            </CardContent>
          </CustomCard>
        </Box>
      </Container>
      
      {/* Delete Confirmation Dialog */}
      <BootstrapDialog
        fullScreen={fullScreen}
        open={deleteModalOpen}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: 20 }}>
          Delete Patient Confirmation
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box display="flex" alignItems="center" mb={2}>
            <WarningIcon color="error" sx={{ fontSize: 48, mr: 2 }} />
            <DialogContentText>
              <Typography fontWeight={500} gutterBottom>
                Are you sure you want to delete this patient?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action will permanently remove all information related to this patient from the system and cannot be undone.
              </Typography>
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCancel} 
            variant="outlined"
            sx={{ borderRadius: 20, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={deletePatient} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 20, textTransform: 'none' }}
          >
            Delete Patient
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
}